import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Download, Search } from 'lucide-react';
import { exportToExcel } from '../../utils/excelExport';
import type { Money, PersonRow, SiteMonth } from './types';
import {
	Card,
	Chip,
	Crumbs,
	DASH,
	ErrorLine,
	FINAL_REASON,
	Loading,
	MonthStepper,
	Tile,
	full,
	fyOf,
	href,
	linkCls,
	monthLabel,
	plain,
	snCls,
	sumOrNull,
	useLoad,
} from './shared';

/* ── Site + month: the people on the final sheet ────────────────────────────── */

export const PEOPLE_COLS: { key: keyof PersonRow; title: string; fmt: (n: Money) => string }[] = [
	{ key: 'days', title: 'Days', fmt: plain },
	{ key: 'basic', title: 'Basic', fmt: full },
	{ key: 'da', title: 'DA', fmt: full },
	{ key: 'hra', title: 'HRA', fmt: full },
	{ key: 'overtime', title: 'OT', fmt: full },
	{ key: 'gross', title: 'Gross', fmt: full },
	{ key: 'pf_ee', title: 'PF (EE)', fmt: full },
	{ key: 'esi_ee', title: 'ESIC (EE)', fmt: full },
	{ key: 'pt', title: 'PT', fmt: full },
	{ key: 'tds', title: 'TDS', fmt: full },
	{ key: 'other_deductions', title: 'Advance / other', fmt: full },
	{ key: 'net_pay', title: 'Net pay', fmt: full },
];

/** A raw sheet value: numbers in Indian grouping, at most 2 decimals; text as written. */
const rawFmt = (v: unknown) =>
	v === null || v === undefined || v === ''
		? DASH
		: typeof v === 'number'
			? v.toLocaleString('en-IN', { maximumFractionDigits: 2 })
			: String(v);
// ponytail: guessed from the header name; a column map flag would be exact.
const addable = (k: string) =>
	!/(^s\.?\s*l?\.?\s*no|serial|sr\.?\s*no|^id$|emp.*(id|code)|uan|esic\s*no|account|ifsc|pan|per\s*day|rate|monthly\s*salary)/i.test(
		k
	);

export const SiteMonthView: React.FC<{ fy: string; site: string; month: string }> = ({
	fy,
	site,
	month,
}) => {
	const { data, error, loading } = useLoad<SiteMonth>(
		`/payroll/site-month?site=${encodeURIComponent(site)}&month=${month}`
	);
	const [find, setFind] = useState('');
	const finals = data?.versions.filter(v => v.is_final) || [];
	const others = data?.versions.filter(v => !v.is_final) || [];
	const fileName = (id: number) => data?.versions.find(v => v.id === id)?.file_name || '';
	const people = (data?.people || []).filter(p =>
		`${p.name} ${p.emp_id || ''}`.toLowerCase().includes(find.toLowerCase())
	);
	const holds = (data?.people || []).filter(p => p.is_hold).length;
	const [view, setView] = useState<'sheet' | 'standard'>('sheet');
	const all = data?.people || [];
	// Standard view hides a column the sheet never fills, rather than a page of dashes.
	const stdCols = PEOPLE_COLS.filter(c => all.some(p => p[c.key] !== null));
	const hiddenCols = PEOPLE_COLS.filter(c => !stdCols.includes(c));
	// Sheet view: every original column, minus the one that just repeats the name (shown as the link).
	const sheetCols = (data?.sheet_columns || []).filter(
		k => !all.length || !all.every(p => p.sheet?.[k] === p.name)
	);
	const isNum = (k: string) => all.some(p => typeof p.sheet?.[k] === 'number');

	// Exports what is on screen: the original columns or the standard ones.
	const exportPeople = () =>
		data &&
		exportToExcel(
			people.map((p, i) => ({ ...p.sheet, ...p, sn: i + 1, is_hold: p.is_hold ? 'On hold' : '' })),
			[
				{ key: 'sn', title: '#' },
				{ key: 'name', title: 'Name' },
				...(view === 'sheet'
					? sheetCols.map(k => ({ key: k, title: k }))
					: [
							{ key: 'emp_id', title: 'Emp ID' },
							...stdCols.map(c => ({ key: c.key, title: c.title })),
						]),
				{ key: 'is_hold', title: 'Hold' },
			],
			`Salary ${site} ${month}`
		);

	return (
		<>
			<Crumbs
				items={[
					{ label: 'Payroll', to: href({ fy }) },
					{ label: site, to: href({ fy, site }) },
					{ label: monthLabel(month) },
				]}
			/>
			<div className='flex items-end justify-between mb-5'>
				<div>
					<h1 className='text-2xl font-semibold text-gray-900'>
						{site} <span className='text-gray-400 font-normal'>·</span> {monthLabel(month)}
					</h1>
					<p className='text-sm text-gray-500'>
						Wage month {monthLabel(month)} ·{' '}
						<Link to={href({ fy, month })} className={linkCls}>
							see all sites this month
						</Link>
					</p>
				</div>
				<MonthStepper month={month} to={m => href({ fy: fyOf(m), site, month: m })} />
			</div>
			{data?.is_summary && (
				<div className='text-sm text-gray-500 mb-3'>
					Summary list of everyone. It repeats staff and contractors, so it is never added to
					totals.
				</div>
			)}
			{loading && <Loading />}
			{error && <ErrorLine msg={error} />}
			{data && !data.totals && (
				<Card className='p-8 text-center text-sm text-gray-500'>
					No final salary sheet for {site} in {monthLabel(month)}. Use the arrows to try another
					month.
				</Card>
			)}
			{data && data.totals && (
				<>
					<div className='grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3'>
						<Tile
							label='Net pay'
							value={full(data.totals.net_pay)}
							sub={`${data.totals.people} people${holds ? ` · ${holds} on hold` : ''}`}
						/>
						<Tile
							label='Gross'
							value={full(data.totals.gross)}
							sub={data.totals.gross === null ? 'not in this sheet' : undefined}
						/>
						<Tile
							label='PF (EPFO)'
							value={full(sumOrNull([data.totals.pf_ee, data.totals.pf_er]))}
							sub={`employee ${full(data.totals.pf_ee)} · employer ${full(data.totals.pf_er)}`}
						/>
						<Tile
							label='ESIC'
							value={full(sumOrNull([data.totals.esi_ee, data.totals.esi_er]))}
							sub={`employee ${full(data.totals.esi_ee)} · employer ${full(data.totals.esi_er)}`}
						/>
					</div>
					<div className='flex gap-6 text-[13px] text-gray-600 mb-4 px-1'>
						<span>
							PT <b className='text-gray-900 font-medium'>{full(data.totals.pt)}</b>
						</span>
						<span>
							TDS <b className='text-gray-900 font-medium'>{full(data.totals.tds)}</b>
						</span>
						<span className='text-gray-400'>A dash means the sheet has no such column.</span>
					</div>

					{/* Where this came from */}
					<Card title='Where this came from' className='mb-4'>
						<div className='px-4 pb-3 text-[13px]'>
							{finals.map(f => (
								<div key={f.id} className='mb-2'>
									<div className='flex flex-wrap items-center gap-2 mb-1.5'>
										<span className='font-medium text-gray-900'>{f.file_name}</span>
										<span className='text-gray-500'>tab {f.tab_name}</span>
										<Chip tone='green'>Final sheet</Chip>
										{f.link && (
											<a
												href={f.link}
												target='_blank'
												rel='noreferrer'
												className={`${linkCls} inline-flex items-center gap-1`}
											>
												Open original <ExternalLink className='w-3 h-3' />
											</a>
										)}
									</div>
									<div className='grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-gray-700'>
										<div>
											<span className='text-gray-500'>Made by</span> {f.maker || DASH}
										</div>
										<div>
											<span className='text-gray-500'>Sent by</span> {f.sender || DASH}
										</div>
										<div>
											<span className='text-gray-500'>Received</span> {f.received_at || DASH}
										</div>
										<div>
											<span className='text-gray-500'>Why final</span>{' '}
											{FINAL_REASON[f.final_reason || ''] || DASH}
										</div>
										{f.sent_to && (
											<div className='col-span-2 md:col-span-4'>
												<span className='text-gray-500'>Sent to</span> {f.sent_to}
											</div>
										)}
										{f.drive_folder && (
											<div className='col-span-2 md:col-span-4'>
												<span className='text-gray-500'>Folder</span> {f.drive_folder}
											</div>
										)}
									</div>
								</div>
							))}
							{others.length > 0 && (
								<details className='mt-2'>
									<summary className={`cursor-pointer ${linkCls}`}>
										{others.length} other version{others.length > 1 ? 's' : ''} found (not used in
										totals)
									</summary>
									<table className='w-full mt-2'>
										<thead className='text-gray-500'>
											<tr>
												<th className='text-left font-medium py-1'>File</th>
												<th className='text-left font-medium'>Tab</th>
												<th className='text-left font-medium'>Received</th>
												<th className='text-left font-medium'>Sent by</th>
												<th className='text-right font-medium'>People</th>
												<th className='text-right font-medium'>Net pay</th>
											</tr>
										</thead>
										<tbody>
											{others.map(v => (
												<tr key={v.id} className='border-t border-gray-100'>
													<td className='py-1'>
														{v.link ? (
															<a href={v.link} target='_blank' rel='noreferrer' className={linkCls}>
																{v.file_name}
															</a>
														) : (
															v.file_name
														)}
													</td>
													<td>{v.tab_name}</td>
													<td>{v.received_at || DASH}</td>
													<td>{v.sender || v.maker || DASH}</td>
													<td className='text-right'>{plain(v.rows_loaded)}</td>
													<td className='text-right'>{full(v.net_pay_total)}</td>
												</tr>
											))}
										</tbody>
									</table>
								</details>
							)}
						</div>
					</Card>

					{/* People */}
					<Card>
						<div className='flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-100'>
							<div className='text-[13px] font-semibold text-gray-800'>People on this sheet</div>
							<div className='flex rounded-lg border border-gray-200 p-0.5 bg-gray-50'>
								{(
									[
										['sheet', `Original sheet · ${sheetCols.length} columns`],
										['standard', 'Standard columns'],
									] as const
								).map(([v, label]) => (
									<button
										key={v}
										onClick={() => setView(v)}
										className={`px-3 py-1 text-[13px] rounded-md transition ${
											view === v
												? 'bg-white shadow-sm text-gray-900 font-medium'
												: 'text-gray-500 hover:text-gray-800'
										}`}
									>
										{label}
									</button>
								))}
							</div>
							<div className='relative'>
								<Search className='w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2' />
								<input
									value={find}
									onChange={e => setFind(e.target.value)}
									placeholder='Find a person or Emp ID'
									className='pl-8 pr-3 py-1.5 text-[13px] border border-gray-200 rounded-lg w-56'
								/>
							</div>
							<button
								onClick={exportPeople}
								className='ml-auto flex items-center gap-1 text-[13px] border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50'
							>
								<Download className='w-4 h-4' /> Export to Excel
							</button>
						</div>
						<div className='px-4 py-2 text-[12px] text-gray-500 border-b border-gray-100'>
							{view === 'sheet'
								? 'Every column exactly as the sheet has it. Click a name to see all their months.'
								: `The same columns for every site, so sites compare.${
										hiddenCols.length
											? ` Not in this sheet: ${hiddenCols.map(c => c.title).join(', ')}.`
											: ''
									} Click a name to see all their months.`}
						</div>
						<div className='overflow-x-auto'>
							<table className='w-full text-[13px]'>
								<thead className='text-gray-500'>
									<tr>
										<th className={snCls}>#</th>
										<th className='text-left px-3 py-2 font-medium sticky left-0 bg-white'>Name</th>
										{view === 'sheet'
											? sheetCols.map(k => (
													<th
														key={k}
														className={`px-3 py-2 font-medium whitespace-nowrap ${isNum(k) ? 'text-right' : 'text-left'}`}
													>
														{k}
													</th>
												))
											: [
													<th key='emp' className='text-left px-3 py-2 font-medium'>
														Emp ID
													</th>,
													...stdCols.map(c => (
														<th
															key={c.key}
															className='text-right px-3 py-2 font-medium whitespace-nowrap'
														>
															{c.title}
														</th>
													)),
												]}
										<th className='px-3 py-2 font-medium' />
									</tr>
								</thead>
								<tbody>
									{people.map((p, i) => (
										<tr
											key={p.id}
											className='border-t border-gray-100 hover:bg-primary-50/50'
											title={`${fileName(p.file_id)} · row ${p.sheet_row}${p.remarks ? ` · ${p.remarks}` : ''}`}
										>
											<td className={snCls}>{i + 1}</td>
											<td className='px-3 py-1.5 whitespace-nowrap sticky left-0 bg-white'>
												<Link
													to={href({ fy, site, month, person: p.name })}
													className={`${linkCls} font-medium`}
												>
													{p.name}
												</Link>
											</td>
											{view === 'sheet'
												? sheetCols.map(k => (
														<td
															key={k}
															className={`px-3 py-1.5 whitespace-nowrap ${isNum(k) ? 'text-right tabular-nums' : 'text-left'} ${
																p.sheet?.[k] == null || p.sheet?.[k] === '' ? 'text-gray-300' : ''
															}`}
														>
															{rawFmt(p.sheet?.[k])}
														</td>
													))
												: [
														<td key='emp' className='px-3 py-1.5 text-gray-500'>
															{p.emp_id || DASH}
														</td>,
														...stdCols.map(c => (
															<td
																key={c.key}
																className={`px-3 py-1.5 text-right whitespace-nowrap ${p[c.key] === null ? 'text-gray-300' : ''} ${
																	c.key === 'net_pay' ? 'font-semibold' : ''
																}`}
															>
																{c.fmt(p[c.key] as Money)}
															</td>
														)),
													]}
											<td className='px-3 py-1.5 text-center'>
												{p.is_hold && <Chip tone='amber'>On hold</Chip>}
											</td>
										</tr>
									))}
									{!people.length && (
										<tr>
											<td colSpan={99} className='px-4 py-6 text-center text-gray-400'>
												No one matches “{find}”
											</td>
										</tr>
									)}
								</tbody>
								<tfoot className='border-t-2 border-gray-200 font-semibold'>
									<tr>
										<td />
										<td className='px-3 py-2 whitespace-nowrap sticky left-0 bg-white'>
											Total · {people.length} people{find ? ' (filtered)' : ''}
										</td>
										{view === 'sheet'
											? sheetCols.map(k => (
													<td
														key={k}
														className='px-3 py-2 text-right whitespace-nowrap tabular-nums'
													>
														{isNum(k) && addable(k)
															? rawFmt(
																	sumOrNull(
																		people.map(p =>
																			typeof p.sheet?.[k] === 'number'
																				? (p.sheet[k] as number)
																				: null
																		)
																	)
																)
															: ''}
													</td>
												))
											: [
													<td key='emp' />,
													...stdCols.map(c => (
														<td key={c.key} className='px-3 py-2 text-right whitespace-nowrap'>
															{c.fmt(sumOrNull(people.map(p => p[c.key] as Money)))}
														</td>
													)),
												]}
										<td />
									</tr>
								</tfoot>
							</table>
						</div>
					</Card>
				</>
			)}
		</>
	);
};
